import type {
  DiFormat,
  Model,
  ModelType,
  QiBoundType,
} from 'types/dca-scenarios-api/responses';
import type {
  ScenarioFormData,
  BiasedProductionStreamConfigData,
  EarlyTimeProductionStreamConfigData,
  ProductionStreamConfigData,
} from './types';
import { getMethodParameterMapping, getParameterRange } from './utils';

const initialValues = {
  qi: null,
  di: null,
  bFactor: null,
  dlim: null,
  ri: null,
  tElf: null,
  m: null,
  n: null,
  eurMbbl: null,
};

const STREAM_KEYS = [
  'oilStreamConfigs',
  'gasStreamConfigs',
  'waterStreamConfigs',
] as const;

export const SECTION_PREFIXES = [
  'globalConfig',
  'generalConfig',
  'segmentationConfig',
  'outlierConfig',
  ...STREAM_KEYS,
] as const;

export const getSectionsWithErrors = (
  sectionNames: string
): Record<string, boolean> => {
  const names = sectionNames ? sectionNames.split(', ') : [];
  const result: Record<string, boolean> = {};
  for (const section of SECTION_PREFIXES) {
    result[section] = names.includes(section);
  }
  return result;
};

function buildSectionsMessage(errors: Record<string, string>): string {
  const errorKeys = Object.keys(errors);
  const sections = new Set<string>();
  for (const key of errorKeys) {
    const section = key.split('.')[0].split('[')[0];
    sections.add(section);
  }
  return Array.from(sections).join(', ');
}

function toFieldPath(path: (string | number)[]): string {
  return path.reduce<string>((acc, segment, i) => {
    const key = String(segment);
    if (/^\d+$/.test(key)) return `${acc}[${key}]`;
    return i === 0 ? key : `${acc}.${key}`;
  }, '');
}

function setFieldError(
  errors: Record<string, string>,
  path: (string | number)[],
  message: string
): void {
  errors[toFieldPath(path)] = message;
}

function validateNumberRange(
  value: number | null | undefined,
  min: number,
  max: number,
  fieldLabel: string,
  options?: { required?: boolean; integerOnly?: boolean; excludeMin?: boolean }
): string | undefined {
  if (value === null || value === undefined) {
    if (options?.required) return `${fieldLabel} is required`;
    return undefined;
  }
  const num = Number(value);
  if (Number.isNaN(num)) return `${fieldLabel} must be a number`;
  if (options?.integerOnly && !Number.isInteger(num)) {
    return `${fieldLabel} must be an integer`;
  }
  if (options?.excludeMin && num <= min) {
    return `${fieldLabel} must be greater than ${min}`;
  }
  if (!options?.excludeMin && num < min) {
    return `${fieldLabel} must be at least ${min}`;
  }
  if (num > max) return `${fieldLabel} must be at most ${max}`;
  return undefined;
}

function validateBoundsOrdering(
  config: ProductionStreamConfigData,
  streamKey: string,
  configIndex: number,
  errors: Record<string, string>
): void {
  const paramKeys = getMethodParameterMapping(config.modelType)[config.model];
  if (!paramKeys?.includes('qi')) return;

  const initialGuess = config.initialGuess as
    Record<string, number> | undefined;
  const lowerBounds = config.lowerBounds as Record<string, number> | undefined;
  const upperBounds = config.upperBounds as Record<string, number> | undefined;

  if (!initialGuess || !lowerBounds || !upperBounds) return;

  const lower = lowerBounds['qi'];
  const guess = initialGuess['qi'];
  const upper = upperBounds['qi'];

  if (lower === null || guess === null || upper === null) return;

  if (lower >= guess) {
    setFieldError(
      errors,
      [streamKey, configIndex, 'lowerBounds', 'qi'],
      'Must be less than initial guess'
    );
  }

  if (guess >= upper) {
    setFieldError(
      errors,
      [streamKey, configIndex, 'upperBounds', 'qi'],
      'Must be greater than initial guess'
    );
  }
}

function validateConfigParams(
  config: ProductionStreamConfigData,
  streamKey: string,
  configIndex: number,
  diFormat: DiFormat,
  errors: Record<string, string>
): void {
  const paramKeys = getMethodParameterMapping(config.modelType)[config.model];
  if (!paramKeys?.length) return;

  const deps = {
    model: config.model as Model,
    modelType: config.modelType as ModelType,
    diFormat,
    qiBoundType: config.qiBoundType as QiBoundType,
  };

  const sections = [
    'initialGuess',
    'lowerBounds',
    'upperBounds',
    'lockedParameters',
  ] as const;

  for (const param of paramKeys) {
    const [min, max] = getParameterRange(param, deps);

    for (const section of sections) {
      const sectionData = { ...initialValues, ...config[section] } as
        Record<string, number | null> | undefined;
      if (!sectionData || !(param in sectionData)) continue;

      const val = sectionData[param];
      const message = validateNumberRange(val, min, max, param, {
        required: section !== 'lockedParameters',
      });
      if (message) {
        setFieldError(
          errors,
          [streamKey, configIndex, section, param],
          message
        );
      }
    }
  }
}

export const onChangeValidation = (value: ScenarioFormData) => {
  const errors: Record<string, string> = {};
  const gc = value.globalConfig;
  const gen = value.generalConfig;
  const seg = value.segmentationConfig;
  const out = value.outlierConfig;

  // --- globalConfig: required + range (skip if not loaded or empty) ---
  if (gc && 'diFormat' in gc) {
    if (!gc.diFormat)
      setFieldError(
        errors,
        ['globalConfig', 'diFormat'],
        'di format is required'
      );
    if (!gc.qiFormat)
      setFieldError(
        errors,
        ['globalConfig', 'qiFormat'],
        'qi format is required'
      );
    if (!gc.durationOption) {
      setFieldError(
        errors,
        ['globalConfig', 'durationOption'],
        'Duration option is required'
      );
    }
    if (!gc.durationFormat) {
      setFieldError(
        errors,
        ['globalConfig', 'durationFormat'],
        'Duration format is required'
      );
    }
    const durationPeriodErr = validateNumberRange(
      gc.durationPeriod,
      1,
      100,
      'Duration period',
      { required: true, integerOnly: true }
    );
    if (durationPeriodErr) {
      setFieldError(
        errors,
        ['globalConfig', 'durationPeriod'],
        durationPeriodErr
      );
    }
  }

  // --- generalConfig: required + range (skip if not loaded or empty) ---
  if (gen && 'historyCutoffMonths' in gen) {
    if (gen.detectPeakRate === null) {
      setFieldError(
        errors,
        ['generalConfig', 'detectPeakRate'],
        'Detect peak rate is required'
      );
    }
    const historyErr = validateNumberRange(
      gen.historyCutoffMonths,
      1,
      Number.MAX_SAFE_INTEGER,
      'History cutoff months',
      { required: true, integerOnly: true }
    );
    if (historyErr)
      setFieldError(
        errors,
        ['generalConfig', 'historyCutoffMonths'],
        historyErr
      );
    const earlyTimeErr = validateNumberRange(
      gen.earlyTimeCutoffMonths,
      1,
      Number.MAX_SAFE_INTEGER,
      'Early time cutoff months',
      { required: true, integerOnly: true }
    );
    if (earlyTimeErr)
      setFieldError(
        errors,
        ['generalConfig', 'earlyTimeCutoffMonths'],
        earlyTimeErr
      );
    if (gen.detectPeakRate === true) {
      const peakErr = validateNumberRange(
        gen.peakRateSearchWindow,
        0,
        1,
        'Peak rate search window',
        { excludeMin: true, required: true }
      );
      if (peakErr)
        setFieldError(
          errors,
          ['generalConfig', 'peakRateSearchWindow'],
          peakErr
        );
    }
  }

  // --- segmentationConfig: required + range (skip if not loaded or empty, validate only method-relevant params) ---
  if (seg && 'method' in seg) {
    if (!seg.method) {
      setFieldError(
        errors,
        ['segmentationConfig', 'method'],
        'Segmentation method is required'
      );
    }
    if (seg.method === 'pelt') {
      const params = seg.parameters ?? {};
      if (params) {
        const penaltyErr = validateNumberRange(
          params.penalty,
          1,
          1000,
          'Penalty',
          {
            integerOnly: true,
          }
        );
        if (penaltyErr)
          setFieldError(
            errors,
            ['segmentationConfig', 'parameters', 'penalty'],
            penaltyErr
          );
        const segMinErr = validateNumberRange(
          params.segmentMinLength,
          1,
          10000,
          'Segment min length',
          { integerOnly: true, required: true }
        );
        if (segMinErr) {
          setFieldError(
            errors,
            ['segmentationConfig', 'parameters', 'segmentMinLength'],
            segMinErr
          );
        }
        const jumpErr = validateNumberRange(
          params.jumpSize,
          1,
          1000,
          'Jump size',
          {
            integerOnly: true,
          }
        );
        if (jumpErr)
          setFieldError(
            errors,
            ['segmentationConfig', 'parameters', 'jumpSize'],
            jumpErr
          );
      }
    }
  }

  // --- outlierConfig: range (skip if not loaded or empty, validate only method-relevant params) ---
  if (out && 'method' in out) {
    const outParams = out.parameters;
    if (outParams) {
      if (out.method === 'elliptic_envelope') {
        const contErr = validateNumberRange(
          outParams.contamination,
          0,
          0.5,
          'Contamination',
          { required: true }
        );
        if (contErr)
          setFieldError(
            errors,
            ['outlierConfig', 'parameters', 'contamination'],
            contErr
          );
      }
      if (out.method === 'n_sigma') {
        const probErr = validateNumberRange(
          outParams.probabilityThreshold,
          0.95,
          1,
          'Probability threshold',
          { required: true }
        );
        if (probErr) {
          setFieldError(
            errors,
            ['outlierConfig', 'parameters', 'probabilityThreshold'],
            probErr
          );
        }
        const numIterErr = validateNumberRange(
          outParams.numIterations,
          1,
          20,
          'Num iterations',
          { integerOnly: true, required: true }
        );
        if (numIterErr) {
          setFieldError(
            errors,
            ['outlierConfig', 'parameters', 'numIterations'],
            numIterErr
          );
        }
      }
    }
  }

  // --- production stream configs: range + valid ---
  const diFormat = gc?.diFormat;
  for (const streamKey of STREAM_KEYS) {
    const configs = value[streamKey] ?? [];
    configs.forEach((config: ProductionStreamConfigData, index: number) => {
      const basePath = [streamKey, index] as (string | number)[];

      const alphaErr = validateNumberRange(config.alpha, 0, 1, 'Alpha');
      if (alphaErr) setFieldError(errors, [...basePath, 'alpha'], alphaErr);

      if (config.modelType === 'biased') {
        const biased = config as BiasedProductionStreamConfigData;
        if (biased.lossFunction === 'mlle') {
          const resErr = validateNumberRange(
            biased.residualSigma,
            0.01,
            1,
            'Residual sigma',
            { required: true }
          );
          if (resErr)
            setFieldError(errors, [...basePath, 'residualSigma'], resErr);
          const probErr = validateNumberRange(
            biased.probabilityDistance,
            0.6,
            1,
            'Probability distance',
            { required: true }
          );
          if (probErr)
            setFieldError(
              errors,
              [...basePath, 'probabilityDistance'],
              probErr
            );
        }
        if (biased.lossFunction === 'mlle' && biased.constrainEur) {
          const eurSections = [
            'initialGuess',
            'lowerBounds',
            'upperBounds',
          ] as const;
          for (const section of eurSections) {
            const sectionData = biased[section] as
              Record<string, number> | undefined;
            const eurMbbl = sectionData?.eurMbbl;
            const eurErr = validateNumberRange(
              eurMbbl,
              1,
              100000000,
              'EUR Mbbl',
              { required: true }
            );
            if (eurErr) {
              setFieldError(errors, [...basePath, section, 'eurMbbl'], eurErr);
            }
          }
        }
      }

      if (config.modelType === 'early_time') {
        const early = config as EarlyTimeProductionStreamConfigData;
        if (early.lossFunction === 'mlle') {
          const resErr = validateNumberRange(
            early.residualSigma,
            0.01,
            1,
            'Residual sigma',
            { required: true }
          );
          if (resErr)
            setFieldError(errors, [...basePath, 'residualSigma'], resErr);
          const probErr = validateNumberRange(
            early.probabilityDistance,
            0.6,
            1,
            'Probability distance',
            { required: true }
          );
          if (probErr)
            setFieldError(
              errors,
              [...basePath, 'probabilityDistance'],
              probErr
            );
        }
        const eurTolErr = validateNumberRange(
          early.eurToleranceFactor,
          0.01,
          1,
          'EUR tolerance factor',
          { required: true }
        );
        if (eurTolErr)
          setFieldError(errors, [...basePath, 'eurToleranceFactor'], eurTolErr);
        const latErr = validateNumberRange(
          early.lateralLengthDegradationFactor,
          0,
          1,
          'Lateral length degradation factor',
          { required: true }
        );
        if (latErr) {
          setFieldError(
            errors,
            [...basePath, 'lateralLengthDegradationFactor'],
            latErr
          );
        }
        const latFtErr = validateNumberRange(
          early.lateralLengthDegradationFt,
          100,
          10000,
          'Lateral length degradation ft',
          { required: true }
        );
        if (latFtErr) {
          setFieldError(
            errors,
            [...basePath, 'lateralLengthDegradationFt'],
            latFtErr
          );
        }
      }

      if (diFormat) {
        validateConfigParams(config, streamKey, index, diFormat, errors);
      }
    });
  }

  if (Object.keys(errors).length === 0) return undefined;
  return { fields: errors, form: buildSectionsMessage(errors) };
};

function findDuplicateMaxMonthsIndices(
  configs: ProductionStreamConfigData[]
): Set<number> {
  const seen = new Map<string, number[]>();
  configs.forEach((config, index) => {
    const key = `${config.modelType}:${config.maxMonthsOnline}`;
    const indices = seen.get(key);
    if (indices) {
      indices.push(index);
    } else {
      seen.set(key, [index]);
    }
  });

  const duplicates = new Set<number>();
  for (const indices of seen.values()) {
    if (indices.length > 1) {
      for (const i of indices) duplicates.add(i);
    }
  }
  return duplicates;
}

export const onDynamicValidation = (value: ScenarioFormData) => {
  const errors: Record<string, string> = {};
  const diFormat = value?.globalConfig?.diFormat;
  const earlyTimeCutoffMonths = value.generalConfig?.earlyTimeCutoffMonths ?? 0;

  if (!diFormat) return undefined;

  for (const streamKey of STREAM_KEYS) {
    const configs = value[streamKey] ?? [];

    const duplicateIndices = findDuplicateMaxMonthsIndices(configs);

    configs.forEach((config: ProductionStreamConfigData, index: number) => {
      const basePath = [streamKey, index] as (string | number)[];

      let maxMonthsErr: string | null = null;

      if (config.maxMonthsOnline < 0)
        return 'Max months online cannot be less than 0';
      if (config.modelType === 'early_time') {
        if (config.maxMonthsOnline > earlyTimeCutoffMonths) {
          maxMonthsErr =
            'Max months online cannot be greater than early time cutoff months';
        }
      } else if (config.maxMonthsOnline < earlyTimeCutoffMonths) {
        maxMonthsErr =
          'Max months online cannot be less than early time cutoff months';
      }

      if (!maxMonthsErr && duplicateIndices.has(index)) {
        maxMonthsErr =
          'MaxMonthsOnline can not be same for same stream and model type';
      }

      if (maxMonthsErr) {
        setFieldError(errors, [...basePath, 'maxMonthsOnline'], maxMonthsErr);
      }

      validateBoundsOrdering(config, streamKey, index, errors);
      validateConfigParams(config, streamKey, index, diFormat, errors);
      return undefined;
    });
  }

  if (Object.keys(errors).length === 0) return undefined;
  return { fields: errors, form: buildSectionsMessage(errors) };
};
